import type { SupabaseClient } from '@supabase/supabase-js';
import { getProdSupabaseClient } from './client';
import { RecallData } from '@/types';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';
import { assertRecallNotTerminal } from '@/lib/lifecycle/terminalGuard';
import {
  REMOVABLE_ASSIGNMENT_STATUSES,
  REMOVAL_BLOCKING_ASSIGNMENT_STATUSES,
  isFinalAssignmentStatus
} from './assignmentStatus';
import {
  LifecycleRefusalError,
  refusalForAssignmentStatus,
  type LifecycleRefusalEvidence
} from '@/lib/errors/lifecycleRefusal';
import { getStopLocationKey } from '@/lib/manualStops/locationKey';

// Global Map to cache getSelectedRecalls results with timestamps
// Prevents duplicate calls within a short window (even sequential calls)
interface CachedResult {
  promise: Promise<Record<string, unknown>[]>;
  timestamp: number;
}
const getSelectedRecallsCache = new Map<string, CachedResult>();
const CACHE_TTL_MS = 2000; // 2 second cache to handle React StrictMode and rapid re-renders

/**
 * Get tenant_id from session or user_tenants table (UUID required for database)
 * Uses getSession() instead of getUser() to avoid 403 errors that log users out
 */
const getTenantId = async (prodClient: SupabaseClient): Promise<string | undefined> => {
  try {
    // First try to get tenant_id from JWT session (most reliable)
    const tenantIdFromSession = await getTenantIdFromSession(prodClient);
    if (tenantIdFromSession) {
      return tenantIdFromSession;
    }

    // Fallback: Query user_tenants table
    // Use getSession() to get user ID without making a network call to /auth/v1/user
    const {
      data: { session }
    } = await prodClient.auth.getSession();
    if (!session?.user?.id) {
      console.warn('[selectedRecalls] No session or user ID available');
      return undefined;
    }

    const { data: userTenants, error } = await prodClient
      .from('user_tenants')
      .select('tenant_id')
      .eq('user_id', session.user.id)
      .limit(1);

    if (error) {
      console.error('[selectedRecalls] Error fetching user_tenants:', error);
      return undefined;
    }

    const tenantId = userTenants?.[0]?.tenant_id;
    return tenantId;
  } catch (error) {
    console.error('[selectedRecalls] Error in getTenantId:', error);
    return undefined;
  }
};

/**
 * Get prod client with synced auth session
 * Ensures the prod client has the same auth session as the main client
 * Since both clients use the same storageKey, they should share the session
 * But we explicitly sync to ensure it's available immediately
 */
/**
 * Get prod client - singleton pattern ensures session is already hydrated
 * The prod client will automatically include Authorization header when making requests
 */
const getProdClientWithSession = async () => {
  // Singleton client is already created and hydrated at app initialization
  // Just return it - session is already in memory
  return getProdSupabaseClient();
};

/**
 * API functions for managing selected recalls in the manual routing system
 */

/**
 * Add a recall to the user's selected recalls
 * @param userId - The user's ID
 * @param recallData - The recall data to add
 * @returns Promise<Object> - The created selected recall record
 */
/** A finite number, or null. Used for the numeric columns, which are nullable. */
function toFiniteNumber(value: unknown, integerOnly = false): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = integerOnly
    ? parseInt(String(value).trim(), 10)
    : parseFloat(String(value).trim());
  if (!Number.isFinite(parsed)) return null;
  if (integerOnly && !Number.isInteger(parsed)) return null;
  return parsed;
}

/**
 * Build the selected_recalls row.
 *
 * Extracted as a pure function so the coercion rules are readable in one place.
 * They were previously spread across the insert literal and four follow-up `if`
 * blocks that mutated it — parse here, then check for NaN there — which is what
 * pushed addSelectedRecall to a cyclomatic complexity of 29.
 */
function buildSelectedRecallInsert(
  userId: string,
  recallData: RecallData,
  tenantId: string,
  dealerStreet: string
): Record<string, unknown> {
  if (!userId || !recallData.vin || !recallData.campaign_number) {
    throw new Error('Missing required fields: user_id, vin, or campaign_number');
  }

  return {
    id: crypto.randomUUID(),
    tenant_id: tenantId,
    user_id: userId,
    vin: recallData.vin,
    campaign_number: recallData.campaign_number,
    car_year: toFiniteNumber(recallData.year, true),
    make_name: recallData.make,
    model_name: recallData.model,
    dealer_name: recallData.seller_name,
    dealer_address: dealerStreet,
    dealer_city: recallData.dealer_city,
    dealer_region: recallData.dealer_region,
    dealer_postal_code: recallData.dealer_postal_code,
    dealer_phone: recallData.dealer_phone,
    dealer_latitude: toFiniteNumber(recallData.dealer_latitude),
    dealer_longitude: toFiniteNumber(recallData.dealer_longitude),
    recall_title: recallData.recall_title,
    stock_number: recallData.stock_number ?? null
  };
}

/* eslint-disable max-lines-per-function */
export const addSelectedRecall = async (userId: string, recallData: RecallData) => {
  try {
    // Validate and fetch missing dealer_street if needed
    let dealerStreet = recallData.dealer_street;

    // If dealer_street is missing, try to fetch it from the source data
    if (!dealerStreet || dealerStreet.trim() === '') {
      // Use RPC function to get tenant-scoped recall data
      // Since we can't query the materialized view directly, we'll use the RPC function
      // For now, we'll skip this lookup and use empty string if dealer_street is missing
      // The RPC function would require tenant_id and might not be efficient for single VIN lookup
      dealerStreet = ''; // Store empty string instead of undefined

      // TODO: If needed, create a function to get single recall by VIN with tenant scoping
      // For now, we'll proceed without the street address lookup
    }

    // Use prod schema for selected_recalls with synced session
    const prodClient = await getProdClientWithSession();

    // Get tenant_id if available (helps with tenant-based RLS policies)
    const tenantId = await getTenantId(prodClient);

    // Refuse to put finished work back into the pool.
    //
    // This function validated tenant, user, VIN and campaign — four checks, none
    // about whether the recall still existed as work. 18 recalls in production
    // were re-selected after being completed, and one of them went on to be
    // re-assigned to the technician who had completed it eight days earlier.
    //
    // Keyed on the lifecycle record rather than the assignment row (P4), so it
    // still refuses when the completing assignment has been deleted.
    await assertRecallNotTerminal(
      recallData.vin,
      recallData.campaign_number,
      tenantId ?? undefined
    );

    // Add tenant_id - column has NOT NULL constraint and requires UUID
    if (!tenantId) {
      throw new Error('Unable to determine tenant association. Please contact support.');
    }

    const insertData = buildSelectedRecallInsert(userId, recallData, tenantId, dealerStreet);

    const { data, error } = await prodClient
      .from('selected_recalls')
      .insert(insertData as never)
      .select()
      .single();

    if (error) {
      console.error('[selectedRecalls] Insert error:', error);
      console.error('[selectedRecalls] Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });

      if (error.code === '23505') {
        // Unique constraint violation - recall already selected by another tenant user
        // This is expected behavior with tenant-scoped deduplication
        console.warn('[selectedRecalls] Duplicate insert attempt:', error);
        throw new Error('This recall has already been added by another team member');
      }
      // System error - translate
      logSystemError(error, 'adding recall');
      throw translateSystemError(error, 'adding recall');
    }

    // Update lifecycle record
    try {
      const { updateLifecycleOnSelection } = await import('./recallLifecycle');
      await updateLifecycleOnSelection(recallData.vin, recallData.campaign_number, userId);
    } catch (lifecycleError) {
      // Log but don't fail the operation
      console.error('[selectedRecalls] Error updating lifecycle on selection:', lifecycleError);
    }

    return data;
  } catch (error) {
    // Check if it's already a user error (preserve those)
    if (error instanceof Error && error.message === 'This recall is already in your route') {
      throw error;
    }
    // System error - translate
    if (isSystemError(error)) {
      logSystemError(error, 'adding recall');
      throw translateSystemError(error, 'adding recall');
    }
    // Re-throw user errors as-is
    throw error;
  }
};

/**
 * Get all selected recalls for the tenant
 * Tenant isolation is enforced via RLS and tenant_id filtering
 * @returns Promise<Array> - Array of selected recalls with skill levels
 */
export const getSelectedRecalls = async () => {
  const prodClient = await getProdClientWithSession();
  const tenantId = await getTenantId(prodClient);

  // CRITICAL: Require tenant_id for security - never return unfiltered data
  if (!tenantId) {
    console.error(
      '[selectedRecalls] No tenant_id available - refusing to query without tenant filter'
    );
    return []; // Return empty array rather than risking cross-tenant data exposure
  }

  // Use tenant_id as cache key for tenant-wide visibility
  const cacheKey = tenantId;
  const now = Date.now();

  // Check if there's a cached result that's still valid
  const cached = getSelectedRecallsCache.get(cacheKey);
  if (cached && now - cached.timestamp < CACHE_TTL_MS) {
    return cached.promise;
  }

  // Create the promise and cache it immediately (synchronously)
  const promise = (async () => {
    try {
      // Build query with tenant filtering (required for tenant isolation)
      const { data, error } = await prodClient
        .from('selected_recalls')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('date_added', { ascending: false });

      if (error) {
        console.error('[selectedRecalls] Error fetching selected recalls:', error);
        logSystemError(error, 'fetching selected recalls');
        throw translateSystemError(error, 'fetching selected recalls');
      }

      return data || [];
    } catch (error) {
      console.error('[selectedRecalls] getSelectedRecalls error:', error);
      // On error, remove from cache so retry can happen
      getSelectedRecallsCache.delete(cacheKey);
      if (isSystemError(error)) {
        logSystemError(error, 'fetching selected recalls');
        throw translateSystemError(error, 'fetching selected recalls');
      }
      throw error;
    }
    // Note: We don't delete on success - let TTL handle expiration
  })();

  // Cache the promise with timestamp immediately (before it resolves)
  getSelectedRecallsCache.set(cacheKey, { promise, timestamp: now });

  return promise;
};

/**
 * Remove a recall from the tenant's selected recalls
 * Any user in the tenant can remove recalls (tenant-scoped visibility)
 * @param vin - The VIN of the recall to remove
 * @param campaignNumber - The campaign number of the recall to remove
 * @returns Promise<Object> - The deleted record
 */
export const removeSelectedRecall = async (vin: string, campaignNumber: string) => {
  try {
    // Refuse if this recall has reached a state worth protecting. Fails closed: an
    // unreadable status returns a 'status_check_failed' refusal rather than proceeding.
    const refusal = await checkRecallRemovable(vin, campaignNumber);
    if (refusal) {
      throw refusal;
    }

    // Use prod schema for both technician_assignments and selected_recalls with synced session
    const prodClient = await getProdClientWithSession();

    // Delete only assignments that are safe to destroy.
    // This is a SAFELIST, not a denylist: anything not named here survives, so a status
    // added later is protected by default rather than deleted by default.
    await prodClient
      .from('technician_assignments')
      .delete()
      .eq('vin', vin)
      .eq('campaign_number', campaignNumber)
      .in('assignment_status', [...REMOVABLE_ASSIGNMENT_STATUSES]);

    // Get tenant_id for tenant isolation
    const tenantId = await getTenantId(prodClient);

    // CRITICAL: Require tenant_id for security
    if (!tenantId) {
      console.error(
        '[selectedRecalls] No tenant_id available - refusing to delete without tenant filter'
      );
      throw new Error('Unable to determine tenant association. Please contact support.');
    }

    // Now delete the selected recall (with tenant filtering, no user_id)
    const { data, error } = await prodClient
      .from('selected_recalls')
      .delete()
      .eq('vin', vin)
      .eq('campaign_number', campaignNumber)
      .eq('tenant_id', tenantId)
      .select();

    if (error) {
      logSystemError(error, 'removing selected recall');
      throw translateSystemError(error, 'removing selected recall');
    }

    // Return first deleted row if any, or null
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'removing selected recall');
      throw translateSystemError(error, 'removing selected recall');
    }
    throw error;
  }
};

/**
 * Clear all selected recalls for the tenant
 * Only clears unconfirmed recalls - confirmed recalls are preserved
 * Any user in the tenant can clear recalls (tenant-scoped visibility)
 * @returns Promise<Object> - The result of the operation
 */
export const clearSelectedRecalls = async () => {
  try {
    // Use prod schema for both technician_assignments and selected_recalls with synced session
    const prodClient = await getProdClientWithSession();

    // Get tenant_id for tenant isolation
    const tenantId = await getTenantId(prodClient);

    // CRITICAL: Require tenant_id for security
    if (!tenantId) {
      console.error(
        '[selectedRecalls] No tenant_id available - refusing to clear without tenant filter'
      );
      throw new Error('Unable to determine tenant association. Please contact support.');
    }

    // Get all selected recalls for this tenant
    const { data, error: fetchError } = await prodClient
      .from('selected_recalls')
      .select('vin, campaign_number')
      .eq('tenant_id', tenantId);

    if (fetchError) {
      logSystemError(fetchError, 'fetching recalls for clear operation');
      throw translateSystemError(fetchError, 'fetching recalls for clear operation');
    }

    // Type assertion for query result
    const selectedRecalls = data as Array<{ vin: string; campaign_number: string }> | null;

    if (!selectedRecalls || selectedRecalls.length === 0) {
      return { success: true };
    }

    // Separate the recalls we may clear from those that must be protected.
    // Anything with a completion, close-out or confirmed route is skipped and counted,
    // so the caller can tell the user how many were left behind and why.
    const removableRecalls: Array<{ vin: string; campaign_number: string }> = [];

    for (const recall of selectedRecalls) {
      const refusal = await checkRecallRemovable(recall.vin, recall.campaign_number);
      if (!refusal) {
        removableRecalls.push(recall);
      }
    }

    // Delete assignments for the removable recalls only
    if (removableRecalls.length > 0) {
      for (const recall of removableRecalls) {
        const { error: deleteError } = await prodClient
          .from('technician_assignments')
          .delete()
          .eq('vin', recall.vin)
          .eq('campaign_number', recall.campaign_number)
          .in('assignment_status', [...REMOVABLE_ASSIGNMENT_STATUSES]); // safelist, not denylist

        if (deleteError && isSystemError(deleteError)) {
          logSystemError(deleteError, 'deleting technician assignments');
          throw translateSystemError(deleteError, 'deleting technician assignments');
        }
      }

      // Delete only the removable selected recalls (tenant-scoped, no user_id filter)
      for (const recall of removableRecalls) {
        const { error } = await prodClient
          .from('selected_recalls')
          .delete()
          .eq('vin', recall.vin)
          .eq('campaign_number', recall.campaign_number)
          .eq('tenant_id', tenantId);

        if (error) {
          logSystemError(error, 'deleting selected recall');
          // Continue with other recalls even if one fails
        }
      }
    }

    // The counts must always reconcile: a bulk action that silently does fewer than it
    // promised is the failure mode to avoid. Callers surface `skipped` to the user.
    const skipped = selectedRecalls.length - removableRecalls.length;
    return {
      success: true,
      cleared: removableRecalls.length,
      total: selectedRecalls.length,
      skipped,
      /** @deprecated use `skipped` — kept so existing callers keep compiling */
      skippedConfirmed: skipped
    };
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'clearing selected recalls');
      throw translateSystemError(error, 'clearing selected recalls');
    }
    throw error;
  }
};

/**
 * Clear only truly unassigned selected recalls for the tenant
 * Unassigned = no matching record in technician_assignments table
 * Any user in the tenant can clear unassigned recalls (tenant-scoped visibility)
 * @returns Promise<Object> - The result of the operation with count of cleared recalls
 */
export const clearUnassignedSelectedRecalls = async () => {
  try {
    const prodClient = await getProdClientWithSession();
    const tenantId = await getTenantId(prodClient);

    // CRITICAL: Require tenant_id for security
    if (!tenantId) {
      console.error(
        '[selectedRecalls] No tenant_id available - refusing to clear without tenant filter'
      );
      throw new Error('Unable to determine tenant association. Please contact support.');
    }

    // 1. Get all selected recalls for this tenant (no user_id filter)
    const { data, error: fetchError } = await prodClient
      .from('selected_recalls')
      .select('id, vin, campaign_number')
      .eq('tenant_id', tenantId);

    if (fetchError) {
      logSystemError(fetchError, 'fetching recalls for clear unassigned operation');
      throw translateSystemError(fetchError, 'fetching recalls for clear unassigned operation');
    }

    // Type assertion for query result
    const selectedRecalls = data as Array<{
      id: string;
      vin: string;
      campaign_number: string;
    }> | null;

    if (!selectedRecalls || selectedRecalls.length === 0) {
      return { success: true, clearedCount: 0 };
    }

    // 2. Get all assignments to identify which recalls have assignments
    const { data: assignmentsData, error: assignmentsError } = await prodClient
      .from('technician_assignments')
      .select('vin, campaign_number');

    if (assignmentsError) {
      logSystemError(assignmentsError, 'fetching assignments for clear unassigned operation');
      throw translateSystemError(
        assignmentsError,
        'fetching assignments for clear unassigned operation'
      );
    }

    // Type assertion for assignments
    const assignments = assignmentsData as Array<{ vin: string; campaign_number: string }> | null;

    // 3. Create a Set of assigned recall keys for O(1) lookup
    const assignedKeys = new Set((assignments || []).map(a => `${a.vin}-${a.campaign_number}`));

    // 4. Filter to only truly unassigned recalls (no assignment record)
    const unassignedRecalls = selectedRecalls.filter(
      recall => !assignedKeys.has(`${recall.vin}-${recall.campaign_number}`)
    );

    if (unassignedRecalls.length === 0) {
      return { success: true, clearedCount: 0 };
    }

    // 5. Delete unassigned recalls
    let clearedCount = 0;
    for (const recall of unassignedRecalls) {
      const { error } = await prodClient.from('selected_recalls').delete().eq('id', recall.id);

      if (!error) {
        clearedCount++;
      } else {
        logSystemError(error, 'deleting unassigned selected recall');
      }
    }

    return { success: true, clearedCount };
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'clearing unassigned selected recalls');
      throw translateSystemError(error, 'clearing unassigned selected recalls');
    }
    throw error;
  }
};

interface BlockingAssignmentRow {
  assignment_status?: string | null;
  completed_at?: string | null;
  completed_by_user_name?: string | null;
}

/**
 * Can this recall be removed, or has it reached a state that must be protected?
 *
 * Replaces `isRecallConfirmed`, which had three problems:
 *   - it checked only the literal 'confirmed', so `completed` sailed straight through
 *   - it used `.maybeSingle()`, which throws when a recall has two assignment rows
 *   - it returned `false` on any query error, so a transient failure unblocked a delete
 *
 * This one FAILS CLOSED. If we cannot determine the state we refuse and say so, because
 * the alternative is destroying a completion record on the strength of a failed lookup.
 * The caller must distinguish "you may not" from "we could not check" — those read very
 * differently to a user.
 *
 * @returns the refusal to surface, or `null` if removal may proceed
 */
export const checkRecallRemovable = async (
  vin: string,
  campaignNumber: string
): Promise<LifecycleRefusalError | null> => {
  try {
    const prodClient = await getProdClientWithSession();

    const { data, error } = await prodClient
      .from('technician_assignments')
      .select('assignment_status, completed_at, completed_by_user_name')
      .eq('vin', vin)
      .eq('campaign_number', campaignNumber)
      .in('assignment_status', [...REMOVAL_BLOCKING_ASSIGNMENT_STATUSES]);

    if (error) {
      logSystemError(error, 'checking whether a recall can be removed');
      return new LifecycleRefusalError('status_check_failed');
    }

    const rows = (data ?? []) as BlockingAssignmentRow[];
    if (rows.length === 0) {
      return null;
    }

    // A recall can carry several assignment rows. Report the most consequential:
    // a completion outranks a confirmation, because it carries a record to protect.
    const blocking =
      rows.find(row => isFinalAssignmentStatus(row.assignment_status)) ?? rows[0] ?? null;
    if (!blocking?.assignment_status) {
      return new LifecycleRefusalError('status_check_failed');
    }

    const evidence: LifecycleRefusalEvidence = {
      ...(blocking.completed_at ? { completedAt: blocking.completed_at } : {}),
      ...(blocking.completed_by_user_name ? { completedBy: blocking.completed_by_user_name } : {})
    };

    return (
      refusalForAssignmentStatus(blocking.assignment_status, evidence) ??
      new LifecycleRefusalError('status_check_failed')
    );
  } catch (error) {
    logSystemError(error, 'checking whether a recall can be removed');
    return new LifecycleRefusalError('status_check_failed');
  }
};

/**
 * Check if a recall is already selected by anyone in the tenant
 * Tenant-scoped: returns true if ANY user in the tenant has selected this recall
 * @param vin - The VIN to check
 * @param campaignNumber - The campaign number to check
 * @returns Promise<boolean> - True if the recall is selected
 */
export const isRecallSelected = async (vin: string, campaignNumber: string) => {
  try {
    // Use prod schema for selected_recalls - session is automatically shared via storageKey
    const prodClient = await getProdClientWithSession();

    // Get tenant_id for tenant isolation
    const tenantId = await getTenantId(prodClient);

    // CRITICAL: Require tenant_id for security
    if (!tenantId) {
      console.error(
        '[selectedRecalls] No tenant_id available - refusing to check without tenant filter'
      );
      return false; // Safe default: assume not selected
    }

    const { data, error } = await prodClient
      .from('selected_recalls')
      .select('id')
      .eq('vin', vin)
      .eq('campaign_number', campaignNumber)
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (error) {
      // Log more details about the error for debugging
      console.error('[selectedRecalls] Error checking if recall is selected:', {
        error,
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        vin,
        campaignNumber
      });
      logSystemError(error, 'checking if recall is selected');
      throw translateSystemError(error, 'checking if recall is selected');
    }

    return !!data;
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'checking if recall is selected');
      throw translateSystemError(error, 'checking if recall is selected');
    }
    throw error;
  }
};

/**
 * Get all staged manual stops for the tenant
 * @returns Promise<Array> - Array of staged manual stops
 */
export const getStagedManualStops = async () => {
  const allRecalls = await getSelectedRecalls();
  return allRecalls.filter((r: Record<string, unknown>) => r['is_manual_stop'] === true);
};

/**
 * Remove a staged manual stop by ID
 * @param id - The ID of the staged manual stop to remove
 * @returns Promise<Object|null> - The deleted record or null
 */
export const removeStagedManualStop = async (id: string) => {
  try {
    const prodClient = await getProdClientWithSession();
    const tenantId = await getTenantId(prodClient);

    // CRITICAL: Require tenant_id for security
    if (!tenantId) {
      console.error(
        '[selectedRecalls] No tenant_id available - refusing to delete without tenant filter'
      );
      throw new Error('Unable to determine tenant association. Please contact support.');
    }

    const { data, error } = await prodClient
      .from('selected_recalls')
      .delete()
      .eq('id', id)
      .eq('is_manual_stop', true)
      .eq('tenant_id', tenantId)
      .select();

    if (error) {
      logSystemError(error, 'removing staged manual stop');
      throw translateSystemError(error, 'removing staged manual stop');
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'removing staged manual stop');
      throw translateSystemError(error, 'removing staged manual stop');
    }
    throw error;
  }
};

/**
 * Get selected recalls grouped by dealer for the tenant
 * @returns Promise<Object> - Object with dealers as keys and arrays of recalls as values
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const getSelectedRecallsGroupedByDealer = async () => {
  try {
    const recalls = await getSelectedRecalls();

    // Group by dealer
    const grouped = recalls.reduce((acc: Record<string, any>, recall: Record<string, any>) => {
      // Customer stops all share dealer_name 'Customer stop' — key them by household.
      const dealerKey =
        recall['stop_kind'] === 'customer' ? getStopLocationKey(recall) : recall['dealer_name'];
      if (!acc[dealerKey]) {
        acc[dealerKey] = {
          dealer: {
            name: recall['dealer_name'],
            address: recall['dealer_address'],
            city: recall['dealer_city'],
            region: recall['dealer_region'],
            phone: recall['dealer_phone'],
            latitude: recall['dealer_latitude'],
            longitude: recall['dealer_longitude']
          },
          recalls: []
        };
      }
      acc[dealerKey].recalls.push(recall);
      return acc;
    }, {});

    return grouped;
  } catch (error) {
    if (isSystemError(error)) {
      logSystemError(error, 'grouping selected recalls by dealer');
      throw translateSystemError(error, 'grouping selected recalls by dealer');
    }
    throw error;
  }
};
